import { AstroCareersViewTransitions } from "@components/AstroViewTransitions"
import CareersCard from "@components/Careers/CareersCard"
import People from "@components/Careers/People"
import { useRouteShellContext } from "@components/routeShellContext"
import { BigHeading } from "@components/SiteShell"
import { SiteShell } from "@components/SiteShell"
import { careersListPageQueryOptions } from "@data/publicPageDataQuery"
import { careersPoints } from "@lib/data"
import * as m from "@paraglide/messages"
import type { CareersListPageData } from "@site-types/publicPageData"
import { createFileRoute } from "@tanstack/react-router"
import { localizedRouteHead } from "@utils/routeHead"
import { getLocalizedCareers } from "@utils/siteData"

export const Route = createFileRoute("/$locale/careers/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(careersListPageQueryOptions(params.locale as "en" | "ja")),
  head: ({ params }) => {
    const locale = params.locale as "en" | "ja"
    return localizedRouteHead({ locale, page: "careers", path: `/${locale}/careers` })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const data = Route.useLoaderData()
  return (
    <SiteShell>
      <CareersListPage data={data} />
    </SiteShell>
  )
}

export function CareersListPage({ data }: { data: CareersListPageData }) {
  const { locale } = useRouteShellContext()
  const careers = getLocalizedCareers(data, locale)
  const { whatWeDo, whyWeDo, gameCompanies, uniqueWorkplace } = careersPoints(locale)
  return (
    <>
      <AstroCareersViewTransitions careers={careers} />
      <BigHeading>{(m.careers_heading_label({}, { locale: locale }) || "Join Us").toUpperCase()}</BigHeading>
      <div className="careers-container">
        <div className="container-people">
          <People />
          <h1>
            {m.careers_titleStart({}, { locale: locale }).toUpperCase()}
            <i className="fa-regular fa-up" />
            {m.careers_titleEnd({}, { locale: locale }).toUpperCase()}
          </h1>
          <div className="careers-header">
            <div className="what">
              <h3>{m.careers_whatWeDo_label({}, { locale: locale }).toUpperCase()}</h3>
              <p>{locale !== "ja" ? m.careers_whatWeDo_subheading({}, { locale: locale }) : ""}</p>
              <ul>{whatWeDo.map((item) => item && <li key={item}>{item}</li>)}</ul>
            </div>
            <div className="why">
              <h3>{m.careers_whyWeDo_label({}, { locale: locale }).toUpperCase()}</h3>
              <ul>{whyWeDo.map((item) => item && <li key={item}>{item}</li>)}</ul>
            </div>
            <div className="unique">
              <h3>{m.careers_uniqueWorkplace_label({}, { locale: locale }).toUpperCase()}</h3>
              <ul>
                <li>
                  {m.careers_uniqueWorkplace_global({}, { locale: locale })}
                  <ul>
                    <li>{gameCompanies}</li>
                  </ul>
                </li>
                {uniqueWorkplace.map((item) => item && <li key={item}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
        <div className="positions">
          <ul>
            {careers.map((position, index) => (
              <CareersCard
                key={`${locale}-${position.slug?.current ?? "career"}-${index}`}
                position={position}
                priority={index === 0}
              />
            ))}
          </ul>
        </div>
      </div>
    </>
  )
}
